import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { MultiSelect } from 'primereact/multiselect';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import api from '../../services/api';
import './CommunityPublish.css';

const CLOUD_NAME = 'dtxar5b36';
const UPLOAD_PRESET = 'bordergarita_uploads';
const UPLOAD_FOLDER = 'community_reports';

const LANGUAGE_OPTIONS = [
  { label: 'Español', value: 'es' },
  { label: 'English', value: 'en' },
];

function waitLabel(mins) {
  if (mins == null) return '—';
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

async function uploadBlobToCloudinary(blob, fileName = 'paste.jpg') {
  const form = new FormData();
  form.append('file', blob, fileName);
  form.append('upload_preset', UPLOAD_PRESET);
  form.append('folder', UPLOAD_FOLDER);
  const res = await fetch(
    `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`,
    { method: 'POST', body: form }
  );
  const json = await res.json();
  if (!res.ok || !json.secure_url) {
    throw new Error(json.error?.message || 'Cloudinary upload failed');
  }
  return json.secure_url;
}

function VerifiedMark() {
  return (
    <span className="cp-verified" title="Verificado" aria-label="Verificado">
      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
        <path
          fill="currentColor"
          d="M23 12l-2.44-2.79.34-3.69-3.61-.82-1.89-3.2L12 2.96 8.6 1.5 6.71 4.69 3.1 5.5l.34 3.7L1 12l2.44 2.79-.34 3.7 3.61.82L8.6 22.5l3.4-1.47 3.4 1.46 1.89-3.19 3.61-.82-.34-3.69L23 12zm-12.91 4.72l-3.8-3.81 1.48-1.48 2.32 2.33 5.85-5.87 1.48 1.48-7.33 7.35z"
        />
      </svg>
    </span>
  );
}

export default function CommunityPublish() {
  const toast = useRef(null);
  const fileInputRef = useRef(null);
  const photoPreviewRef = useRef('');
  const [meta, setMeta] = useState(null);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const [entryPageIds, setEntryPageIds] = useState([]);
  const [port, setPort] = useState('');
  const [crossingName, setCrossingName] = useState('');
  const [laneType, setLaneType] = useState('standard');
  const [transportMode, setTransportMode] = useState('vehicle');
  const [language, setLanguage] = useState('es');
  const [text, setText] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [waitBucketMinutes, setWaitBucketMinutes] = useState(null);
  const [waitingFromPositionMinutes, setWaitingFromPositionMinutes] =
    useState(null);
  const [asCommunityPersona, setAsCommunityPersona] = useState(false);
  const [notifyCity, setNotifyCity] = useState(false);
  const [personaName, setPersonaName] = useState('');
  const [generatingName, setGeneratingName] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/community_reports/publish/meta');
        setMeta(data);
      } catch {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudo cargar el módulo de publicación',
        });
      } finally {
        setLoadingMeta(false);
      }
    })();
  }, []);

  useEffect(() => {
    return () => {
      if (photoPreviewRef.current) {
        URL.revokeObjectURL(photoPreviewRef.current);
      }
    };
  }, []);

  const entryPageOptions = useMemo(
    () =>
      (meta?.entryPages || []).map((p) => ({
        ...p,
        value: p.id,
        label: p.label || `[${p.lang || '?'}] ${p.fullSlug || p.title}`,
      })),
    [meta]
  );

  const waitOptions = useMemo(
    () =>
      (meta?.waitBuckets || []).map((m) => ({
        label: waitLabel(m),
        value: m,
      })),
    [meta]
  );

  const setLocalPhoto = useCallback((file) => {
    if (!file || !file.type?.startsWith('image/')) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Foto',
        detail: 'Selecciona una imagen válida',
      });
      return;
    }
    if (photoPreviewRef.current) {
      URL.revokeObjectURL(photoPreviewRef.current);
    }
    const localUrl = URL.createObjectURL(file);
    photoPreviewRef.current = localUrl;
    setPhotoFile(file);
    setPhotoPreview(localUrl);
  }, []);

  const pasteFromClipboard = useCallback(async () => {
    try {
      if (!navigator.clipboard?.read) {
        toast.current?.show({
          severity: 'warn',
          summary: 'Clipboard',
          detail:
            'Tu navegador no permite leer el clipboard. Usa Ctrl/Cmd+V en el área de foto.',
        });
        return;
      }
      const items = await navigator.clipboard.read();
      for (const item of items) {
        const type = item.types.find((t) => t.startsWith('image/'));
        if (!type) continue;
        const blob = await item.getType(type);
        const file = new File(
          [blob],
          `clipboard.${type.split('/')[1] || 'png'}`,
          { type }
        );
        setLocalPhoto(file);
        return;
      }
      toast.current?.show({
        severity: 'warn',
        summary: 'Clipboard',
        detail: 'No hay imagen en el clipboard',
      });
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Clipboard',
        detail: err?.message || 'No se pudo leer el clipboard',
      });
    }
  }, [setLocalPhoto]);

  useEffect(() => {
    const onPaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.type?.startsWith('image/')) {
          e.preventDefault();
          const file = item.getAsFile();
          if (file) setLocalPhoto(file);
          return;
        }
      }
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [setLocalPhoto]);

  const generatePersonaName = useCallback(async () => {
    try {
      setGeneratingName(true);
      const { data } = await api.get('/community_reports/publish/random-name', {
        params: { lang: language },
      });
      if (data?.name) setPersonaName(data.name);
    } catch {
      toast.current?.show({
        severity: 'error',
        summary: 'Nickname',
        detail: 'No se pudo generar un nombre',
      });
    } finally {
      setGeneratingName(false);
    }
  }, [language]);

  useEffect(() => {
    if (asCommunityPersona && !personaName) {
      generatePersonaName();
    }
  }, [asCommunityPersona, personaName, generatePersonaName]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) setLocalPhoto(file);
  };

  const clearPhoto = () => {
    if (photoPreviewRef.current) {
      URL.revokeObjectURL(photoPreviewRef.current);
      photoPreviewRef.current = '';
    }
    setPhotoFile(null);
    setPhotoPreview('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const canPublish =
    entryPageIds.length > 0 &&
    Boolean(laneType && transportMode) &&
    !publishing;

  const publish = async () => {
    if (!canPublish) return;
    try {
      setPublishing(true);
      let photoUrl;
      if (photoFile) {
        photoUrl = await uploadBlobToCloudinary(
          photoFile,
          photoFile.name || 'photo.jpg'
        );
      }
      const payload = {
        port: port.trim() || undefined,
        laneType,
        transportMode,
        crossingName: crossingName.trim() || undefined,
        entryPageIds,
        entryPageId: entryPageIds[0],
        text: text.trim(),
        photoUrl: photoUrl || undefined,
        waitBucketMinutes,
        waitingFromPositionMinutes,
        language,
        publishImmediately: true,
        asCommunityPersona,
        notifyCity: !asCommunityPersona && notifyCity,
        personaName: asCommunityPersona
          ? personaName.trim() || undefined
          : undefined,
      };
      const { data } = await api.post('/community_reports/publish', payload);
      toast.current?.show({
        severity: 'success',
        summary: 'Publicado',
        detail:
          data.message ||
          (asCommunityPersona
            ? `Publicado como ${data.personaName || personaName}`
            : 'Post de BorderGarita publicado en comunidad'),
      });
      setText('');
      clearPhoto();
      setWaitBucketMinutes(null);
      setWaitingFromPositionMinutes(null);
      if (asCommunityPersona) {
        generatePersonaName();
      }
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail:
          err?.response?.data?.error ||
          err?.response?.data?.detail ||
          err?.message ||
          'No se pudo publicar',
      });
    } finally {
      setPublishing(false);
    }
  };

  return (
    <Card className="cp-card">
      <div className="cp-page">
        <Toast ref={toast} />

        <header className="cp-header">
          <div>
            <p className="cp-kicker">Community & Mobile</p>
            <h2 className="cp-title">
              {asCommunityPersona ? (
                <>
                  Publicar como{' '}
                  <span className="cp-brand cp-brand-persona">
                    {personaName || 'vecino'}
                  </span>
                </>
              ) : (
                <>
                  Publicar como{' '}
                  <span className="cp-brand">
                    BorderGarita
                    <VerifiedMark />
                  </span>
                </>
              )}
            </h2>
            <p className="cp-sub">
              {asCommunityPersona
                ? 'Sale en la app como un reporte normal de comunidad (sin badge verificado). Ideal para sembrar actividad al inicio.'
                : 'Publicaciones oficiales con la misma estructura de un reporte: comentario, foto y tiempos de espera. Salen verificadas en la app.'}
            </p>
          </div>
          <Tag
            severity={asCommunityPersona ? 'secondary' : 'info'}
            value={
              asCommunityPersona
                ? personaName || 'Vecino'
                : meta?.author?.displayName || 'BorderGarita'
            }
            icon={asCommunityPersona ? 'pi pi-user' : 'pi pi-verified'}
          />
        </header>

        {loadingMeta ? (
          <div className="cp-loading">
            <i className="pi pi-spin pi-spinner" />
          </div>
        ) : (
          <div className="cp-grid">
            <section className="cp-panel">
              <h3 className="cp-section-title">Autor</h3>
              <div className="cp-fields">
                <label className="cp-check-row">
                  <Checkbox
                    inputId="asCommunityPersona"
                    checked={asCommunityPersona}
                    onChange={(e) => setAsCommunityPersona(Boolean(e.checked))}
                  />
                  <span>
                    Publicar como vecino sintético (sin badge verificado)
                  </span>
                </label>
                {asCommunityPersona ? (
                  <label>
                    Nickname
                    <div className="cp-name-row">
                      <InputText
                        value={personaName}
                        onChange={(e) => setPersonaName(e.target.value)}
                        placeholder="Ej. TacoGarita"
                        className="w-full"
                      />
                      <Button
                        type="button"
                        icon="pi pi-refresh"
                        label="Generar"
                        outlined
                        loading={generatingName}
                        onClick={generatePersonaName}
                      />
                    </div>
                  </label>
                ) : (
                  <label className="cp-check-row">
                    <Checkbox
                      inputId="notifyCity"
                      checked={notifyCity}
                      onChange={(e) => setNotifyCity(Boolean(e.checked))}
                    />
                    <span>
                      Avisar a quien sigue esta ciudad y tiene este cruce prendido
                    </span>
                  </label>
                )}
              </div>
            </section>

            <section className="cp-panel">
              <h3 className="cp-section-title">Cruce y carril</h3>
              <div className="cp-fields">
                <label>
                  Entry pages
                  <MultiSelect
                    value={entryPageIds}
                    options={entryPageOptions}
                    onChange={(e) => setEntryPageIds(e.value || [])}
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Selecciona una o más entry pages"
                    filter
                    display="chip"
                    className="w-full"
                    selectedItemsLabel="{0} entry pages"
                  />
                </label>
                <div className="cp-row2">
                  <label>
                    Port / nombre
                    <InputText
                      value={port}
                      onChange={(e) => setPort(e.target.value)}
                      placeholder="Opcional — ej. San Ysidro"
                      className="w-full"
                    />
                  </label>
                  <label>
                    Crossing name
                    <InputText
                      value={crossingName}
                      onChange={(e) => setCrossingName(e.target.value)}
                      placeholder="Opcional — nombre visible"
                      className="w-full"
                    />
                  </label>
                </div>
                <div className="cp-row3">
                  <label>
                    Carril
                    <Dropdown
                      value={laneType}
                      options={meta?.laneTypes || []}
                      onChange={(e) => setLaneType(e.value)}
                      className="w-full"
                    />
                  </label>
                  <label>
                    Transporte
                    <Dropdown
                      value={transportMode}
                      options={meta?.transportModes || []}
                      onChange={(e) => setTransportMode(e.value)}
                      className="w-full"
                    />
                  </label>
                  <label>
                    Idioma de la publicación
                    <Dropdown
                      value={language}
                      options={LANGUAGE_OPTIONS}
                      onChange={(e) => setLanguage(e.value)}
                      className="w-full"
                    />
                  </label>
                </div>
              </div>
            </section>

            <section className="cp-panel">
              <h3 className="cp-section-title">Comentario y esperas</h3>
              <div className="cp-fields">
                <label>
                  Comentario
                  <InputTextarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    rows={5}
                    autoResize
                    placeholder="Escribe el mensaje oficial para la comunidad…"
                    className="w-full"
                  />
                </label>
                <div className="cp-row2">
                  <label>
                    Espera a cruzar
                    <Dropdown
                      value={waitBucketMinutes}
                      options={waitOptions}
                      onChange={(e) => setWaitBucketMinutes(e.value)}
                      placeholder="Opcional"
                      showClear
                      className="w-full"
                    />
                  </label>
                  <label>
                    Esperando desde aquí
                    <Dropdown
                      value={waitingFromPositionMinutes}
                      options={waitOptions}
                      onChange={(e) => setWaitingFromPositionMinutes(e.value)}
                      placeholder="Opcional"
                      showClear
                      className="w-full"
                    />
                  </label>
                </div>
              </div>
            </section>

            <section className="cp-panel cp-photo-panel">
              <div className="cp-photo-head">
                <h3 className="cp-section-title">Foto</h3>
                <div className="cp-photo-actions">
                  <Button
                    type="button"
                    label="Paste from clipboard"
                    icon="pi pi-clipboard"
                    className="p-button-outlined p-button-sm"
                    onClick={pasteFromClipboard}
                    disabled={publishing}
                  />
                  <Button
                    type="button"
                    label="Seleccionar archivo"
                    icon="pi pi-upload"
                    className="p-button-outlined p-button-sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={publishing}
                  />
                  {photoPreview && (
                    <Button
                      type="button"
                      label="Quitar"
                      icon="pi pi-times"
                      className="p-button-text p-button-danger p-button-sm"
                      onClick={clearPhoto}
                    />
                  )}
                </div>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) setLocalPhoto(file);
                }}
              />

              <div
                className={`cp-dropzone${dragOver ? ' is-drag' : ''}${
                  photoPreview ? ' has-photo' : ''
                }`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
              >
                {photoPreview ? (
                  <img
                    src={photoPreview}
                    alt="Preview"
                    className="cp-preview"
                  />
                ) : (
                  <div className="cp-drop-empty">
                    <i className="pi pi-images" />
                    <strong>Arrastra una foto aquí</strong>
                    <span>
                      Solo preview local · se sube a Cloudinary al publicar
                    </span>
                  </div>
                )}
              </div>
            </section>

            <section className="cp-preview-card">
              <p className="cp-kicker">Preview en app</p>
              <div className="cp-feed-preview">
                <div className="cp-avatar">
                  {asCommunityPersona
                    ? (personaName || 'V').slice(0, 2).toUpperCase()
                    : 'BG'}
                </div>
                <div>
                  <div className="cp-feed-name">
                    {asCommunityPersona
                      ? personaName || 'Vecino'
                      : 'BorderGarita'}
                    {!asCommunityPersona ? <VerifiedMark /> : null}
                    <span className="cp-feed-time">ahora</span>
                  </div>
                  {text ? (
                    <p className="cp-feed-text">{text}</p>
                  ) : (
                    <p className="cp-feed-text muted">Sin comentario aún…</p>
                  )}
                  {photoPreview && (
                    <img
                      src={photoPreview}
                      alt=""
                      className="cp-feed-photo"
                    />
                  )}
                </div>
              </div>
              <div className="cp-publish-bar">
                <Button
                  label={
                    asCommunityPersona
                      ? 'Publicar como vecino'
                      : 'Publicar en comunidad'
                  }
                  icon="pi pi-send"
                  className="p-button-primary"
                  loading={publishing}
                  disabled={!canPublish}
                  onClick={publish}
                />
              </div>
            </section>
          </div>
        )}
      </div>
    </Card>
  );
}
